import { useState, useEffect } from 'react';
import { getKidsPublicForm, submitKidsPublicAvailability } from '../../utils/api';

// Public "tell us your Kids Conference plans" form — no login, no personal
// link needed. Meant for the whole serving roster INCLUDING people who have
// not registered yet: the backend matches by email (then name); no match
// creates a Not Registered person on the Kids roster so the team can chase
// their registration. Teal-branded like every Kids surface.

const TEAL = '#6AA292';

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  if (isNaN(h)) return t;
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function KidsPublicAvailabilityPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [maxSessions, setMaxSessions] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    getKidsPublicForm()
      .then(d => setData(d))
      .catch(() => setError('This form is not available right now — contact the Kids team.'))
      .finally(() => setLoading(false));
  }, []);

  const sessions = data?.sessions || [];
  const days = [];
  sessions.forEach(s => { if (!days.includes(s.day)) days.push(s.day); });

  function toggle(blockId) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId); else next.add(blockId);
      return next;
    });
  }

  async function handleSubmit() {
    if (!name.trim() || !email.trim()) return;
    setSubmitting(true);
    try {
      const r = await submitKidsPublicAvailability({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        maxSessions,
        selected: Array.from(selected),
      });
      setDone(r);
    } catch (e) {
      setError('Something went wrong — try again or contact the Kids team.');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = name.trim() && /\S+@\S+\.\S+/.test(email.trim()) && maxSessions > 0 && selected.size > 0;

  const header = (
    <div style={{ background: TEAL, padding: '26px 24px', textAlign: 'center' }}>
      <div style={{ fontFamily: 'var(--font-h)', fontWeight: 900, color: '#fff', fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>The Belonging Co</div>
      <div style={{ fontFamily: 'var(--font-h)', fontWeight: 900, color: '#fff', fontSize: 24, marginTop: 4 }}>Kids Conference — Serving Plans</div>
      <div style={{ fontFamily: 'var(--font-b)', fontSize: 14, color: 'rgba(255,255,255,0.9)', marginTop: 4 }}>Tell us how much you can serve — it takes about a minute.</div>
    </div>
  );

  if (loading) {
    return <div style={{ minHeight: '100vh', background: 'var(--off)' }}>{header}<div style={{ textAlign: 'center', padding: 60, fontFamily: 'var(--font-b)', color: 'var(--muted)' }}>Loading…</div></div>;
  }
  if (error) {
    return <div style={{ minHeight: '100vh', background: 'var(--off)' }}>{header}<div style={{ textAlign: 'center', padding: 60, fontFamily: 'var(--font-b)', color: 'var(--muted)', maxWidth: 420, margin: '0 auto', lineHeight: 1.6 }}>{error}</div></div>;
  }
  if (done) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--off)' }}>
        {header}
        <div style={{ maxWidth: 520, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 24, fontWeight: 900, color: 'var(--black)', marginBottom: 10 }}>Thank you!</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 15, color: 'var(--mid)', lineHeight: 1.7 }}>
            Your plans are in — you can serve up to <strong>{maxSessions}</strong> session{maxSessions === 1 ? '' : 's'} and
            you're free for <strong>{selected.size}</strong>. The Kids team will send your schedule once it's built.
            {done.created && (
              <>
                <br /><br />
                We didn't find a conference registration under <strong>{email.trim()}</strong> yet — if you haven't
                registered, please do (use this same email so everything links up).
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off)' }}>
      {header}
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '32px 24px 80px' }}>
        <div className="avail-form-card">
          <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 10 }}>Who are you?</div>
          <div className="form-group"><label className="form-label">Name</label><input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="First and last name" /></div>
          <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email address" />
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 5 }}>
              Use the email you'll register with (or already registered with) — that's how we match you up.
            </div>
          </div>
          <div className="form-group"><label className="form-label">Phone (optional)</label><input className="form-input" value={phone} onChange={e => setPhone(e.target.value)} /></div>

          <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', margin: '22px 0 8px' }}>How many sessions can you serve?</div>
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--mid)', marginBottom: 10, lineHeight: 1.6 }}>
            Pick a number first, then check every session you're free for — even more than your number.
            We'll schedule you into the most helpful ones.
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
            {Array.from({ length: Math.min(sessions.length || 4, 8) }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => setMaxSessions(n)} style={{ fontFamily: 'var(--font-h)', fontWeight: 800, minWidth: 42, padding: '8px 12px', borderRadius: 6, cursor: 'pointer', border: `2px solid ${maxSessions === n ? TEAL : 'var(--border)'}`, background: maxSessions === n ? TEAL : '#fff', color: maxSessions === n ? '#fff' : 'var(--mid)' }}>{n}</button>
            ))}
          </div>

          <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>Which sessions are you free for?</div>
          {sessions.length === 0 && (
            <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
              Sessions haven't been posted yet — check back soon.
            </div>
          )}
          {days.map(day => (
            <div key={day} style={{ marginBottom: 10 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 800, color: 'var(--black)', margin: '10px 0 6px' }}>{day}</div>
              {sessions.filter(s => s.day === day).map(s => {
                const on = selected.has(s.blockId);
                return (
                  <label key={s.blockId} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 6, cursor: 'pointer', background: on ? 'rgba(106,162,146,0.1)' : '#fff' }}>
                    <input type="checkbox" checked={on} onChange={() => toggle(s.blockId)} style={{ width: 18, height: 18, accentColor: TEAL }} />
                    <span style={{ fontFamily: 'var(--font-h)', fontSize: 14, fontWeight: 700 }}>{s.blockTitle}</span>
                    <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
                      {[
                        s.startTime ? `${fmtTime(s.startTime)}${s.endTime ? ` – ${fmtTime(s.endTime)}` : ''}` : '',
                        s.period ? s.period.charAt(0).toUpperCase() + s.period.slice(1).toLowerCase() : '',
                      ].filter(Boolean).join(' · ')}
                    </span>
                  </label>
                );
              })}
            </div>
          ))}

          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            style={{ marginTop: 14, fontFamily: 'var(--font-h)', fontWeight: 800, fontSize: 14, padding: '12px 22px', border: 'none', borderRadius: 6, cursor: canSubmit ? 'pointer' : 'default', background: canSubmit ? '#FFDB7C' : 'var(--border)', color: canSubmit ? '#202020' : 'var(--muted)' }}
          >
            {submitting ? 'Submitting…' : 'Submit My Plans'}
          </button>
        </div>
      </div>
    </div>
  );
}
